/**
 * Brand-aware Mermaid.js configuration
 *
 * Reads CSS vars set by AppearanceProvider so Mermaid clusters/labels
 * follow the active brand (Martha coral / Archie blue). Subscribes to
 * `data-brand` changes via a MutationObserver and re-initialises mermaid
 * with a fresh theme so existing pages re-render with new colors.
 *
 * Diagram-option palettes (git/pie/sequence series) stay literal — they
 * are visualization options, not chrome.
 */

import mermaid from 'mermaid';
import { getBrandColor, subscribeBrandChange } from '../utils/brandColor';

export function buildMermaidTheme() {
  const surfaceMuted = getBrandColor('surface-muted');
  const borderDefault = getBrandColor('border-default');
  const coral500 = getBrandColor('coral-500');
  const coral100 = getBrandColor('coral-100');

  return {
    theme: 'base' as const,
    themeVariables: {
      // General
      fontFamily: '"Inter", system-ui, sans-serif',
      fontSize: '14px',

      // Primary colors - brand-tinted
      primaryColor: '#FECDD3',
      primaryTextColor: '#9F1239',
      primaryBorderColor: coral500,

      // Secondary colors - Amber
      secondaryColor: '#FEF3C7',
      secondaryTextColor: '#92400E',
      secondaryBorderColor: '#F59E0B',

      // Tertiary colors - Blue
      tertiaryColor: '#DBEAFE',
      tertiaryTextColor: '#1E40AF',
      tertiaryBorderColor: '#3B82F6',

      // Lines and edges
      lineColor: '#9CA3AF',
      textColor: '#374151',

      // Background — brand-aware
      mainBkg: '#FFFFFF',
      nodeBkg: coral100,
      clusterBkg: surfaceMuted,
      clusterBorder: borderDefault,

      // Flowchart specific
      nodeTextColor: '#1F2937',

      // Sequence diagram
      actorBkg: coral500,
      actorTextColor: '#FFFFFF',
      actorLineColor: '#9CA3AF',
      signalColor: '#374151',
      signalTextColor: '#374151',
      labelBoxBkgColor: surfaceMuted,
      labelBoxBorderColor: borderDefault,
      labelTextColor: '#374151',
      loopTextColor: '#374151',
      noteBkgColor: '#FEF3C7',
      noteBorderColor: '#F59E0B',
      noteTextColor: '#92400E',
      activationBkgColor: coral100,
      activationBorderColor: coral500,

      // Gantt
      gridColor: borderDefault,
      doneTaskBkgColor: '#10B981',
      doneTaskBorderColor: '#059669',
      activeTaskBkgColor: coral500,
      activeTaskBorderColor: '#DC2626',
      taskBkgColor: coral100,
      taskBorderColor: coral500,
      taskTextColor: '#1F2937',
      taskTextLightColor: '#FFFFFF',
      taskTextOutsideColor: '#374151',
      todayLineColor: coral500,

      // State diagram
      labelColor: '#374151',
      altBackground: '#F3F4F6',

      // Class diagram
      classText: '#1F2937',

      // Git graph
      git0: coral500,
      git1: '#3B82F6',
      git2: '#10B981',
      git3: '#F59E0B',
      git4: '#8B5CF6',
      git5: '#EC4899',
      git6: '#14B8A6',
      git7: '#EF4444',
      gitBranchLabel0: '#FFFFFF',
      gitBranchLabel1: '#FFFFFF',
      gitBranchLabel2: '#FFFFFF',
      gitInv0: '#9F1239',
      gitInv1: '#1E40AF',
      gitInv2: '#065F46',

      // Pie chart
      pie1: coral500,
      pie2: '#3B82F6',
      pie3: '#10B981',
      pie4: '#F59E0B',
      pie5: '#8B5CF6',
      pie6: '#EC4899',
      pie7: '#14B8A6',
      pie8: '#EF4444',
      pie9: '#6366F1',
      pie10: '#84CC16',
      pie11: '#F97316',
      pie12: '#06B6D4',
      pieOuterStrokeWidth: '2px',
    },
  };
}

let isInitialized = false;
let unsubscribeBrand: (() => void) | null = null;

function applyMermaidConfig() {
  mermaid.initialize({
    startOnLoad: false,
    ...buildMermaidTheme(),
    securityLevel: 'loose',
  });
}

export function initializeMermaid() {
  if (isInitialized) return;
  applyMermaidConfig();
  isInitialized = true;

  // Re-init on brand change so theme vars get re-read.
  if (!unsubscribeBrand) {
    unsubscribeBrand = subscribeBrandChange(() => {
      applyMermaidConfig();
      window.dispatchEvent(new CustomEvent('mermaid-theme-change'));
    });
  }
}

// Backwards-compat default export — callers using `marthaMermaidTheme`
// directly get a snapshot of the current brand theme.
export const marthaMermaidTheme = buildMermaidTheme();
export default marthaMermaidTheme;
